import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import IRequestError from '../../../dtos/IRequestError';
import { useAuth } from '../../../hooks/auth';
import { useToast } from '../../../hooks/toast';
import api from '../../../services/api';
import { formatTournamentDateRange } from '../../../utils/scheduleDate';
import PrimaryButton from '../../../components/Buttons/PrimaryButton';
import CreateTournamentButton, {
  TournamentSchoolNotice,
} from '../../../components/Buttons/CreateTournamentButton';
import TournamentsBriefing from '../TournamentsBriefing';
import { StatusPill } from '../../../components/Tournament';
import {
  BlobIllustration,
  MiniBotIcon,
  TrophyIcon,
} from '../../../assets/svg/SVGComponents';
import {
  CATEGORY_NUMBER_TO_LABEL,
  MODALITY_SLUG_TO_NAME,
} from '../../Tournament/CreateTournament';
import {
  ActionButtonsRow,
  CategoryBadge,
  CategoryBadgeRow,
  Container,
  HeaderContent,
  LoadingWrapper,
  MainCard,
  MainCardContent,
  ModalitiesLabel,
  ModalitiesRow,
  ModalityCard,
  ModalityImage,
  ModalityInfo,
  ModalityMeta,
  ModalityName,
  Spinner,
  TournamentInfo,
  TournamentList,
  TournamentMeta,
  StatusSlot,
  TournamentTitle,
  TrophyImageWrapper,
} from './styles';

interface ITournamentCategory {
  id: string;
  number: number;
  name?: string;
}

interface ITournamentModality {
  id: string;
  modality: string;
  max_rounds?: number;
  time_limit_seconds?: number;
  categories?: ITournamentCategory[];
}

interface ITournamentListItem {
  id: string;
  name: string;
  date?: string | null;
  end_date?: string | null;
  school_id?: string | null;
  icon_url?: string | null;
  color?: string | null;
  status?: string | null;
  modalities: ITournamentModality[];
}

const MODALITY_IMAGES: Record<string, string> = {
  robo_pintor:
    'https://fuzzyplaydev.s3.us-east-2.amazonaws.com/illustration+clean/Group+14045%402x.png',
  seguidor_de_linha:
    'https://fuzzyplaydev.s3.us-east-2.amazonaws.com/illustration+clean/illustration-seguidor+de+trilha-2x.png',
};

const TROPHY_IMAGE =
  'https://fuzzyplaydev.s3.us-east-2.amazonaws.com/illustration+clean/trophy-podium.png';

const formatRoundDuration = (timeLimitSeconds?: number): string => {
  if (!timeLimitSeconds || timeLimitSeconds <= 0) {
    return '-';
  }
  if (timeLimitSeconds < 60) {
    return `${timeLimitSeconds}s`;
  }
  const minutes = Math.floor(timeLimitSeconds / 60);
  const seconds = timeLimitSeconds % 60;
  if (seconds === 0) {
    return `${minutes} min`;
  }
  return `${minutes} min ${seconds}s`;
};

const ListTournaments: React.FC = () => {
  const navigate = useNavigate();
  const { addToast } = useToast();
  const { user, isSessionExpired } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [tournaments, setTournaments] = useState<ITournamentListItem[]>([]);

  const loadTournaments = useCallback(async () => {
    if (!user.school?.id) {
      setTournaments([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);

    try {
      const response = await api.get<ITournamentListItem[]>('/tournaments', {
        params: { school_id: user.school.id },
      });

      setTournaments(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      if (!(await isSessionExpired(err as IRequestError))) {
        addToast({
          type: 'error',
          expression: 'Ops.',
          description: 'Não foi possível carregar os torneios.',
        });
      }
    } finally {
      setIsLoading(false);
    }
  }, [addToast, isSessionExpired, user.school?.id]);

  useEffect(() => {
    loadTournaments();
  }, [loadTournaments]);

  if (isLoading) {
    return (
      <Container>
        <LoadingWrapper>
          <Spinner />
        </LoadingWrapper>
      </Container>
    );
  }

  if (tournaments.length === 0) {
    return <TournamentsBriefing />;
  }

  return (
    <Container>
      <HeaderContent>
        <TrophyIcon width="2rem" stroke="#FF9800" strokeWidth={3} fill="none" />
        <h1>Torneios</h1>
        <CreateTournamentButton label="Criar um torneio local" />
      </HeaderContent>

      <TournamentSchoolNotice />

      <TournamentList>
        {tournaments.map(tournament => {
          // Mesmo formatador da página do torneio: horário de parede em UTC (ver
          // utils/scheduleDate). Antes a lista convertia pelo fuso do navegador
          // e mostrava 3h a menos que a página do torneio.
          const dateLabel = formatTournamentDateRange(
            tournament.date,
            tournament.end_date,
          );
          return (
            <MainCard key={tournament.id}>
              <MainCardContent>
                <TrophyImageWrapper>
                  <img src={tournament.icon_url ?? TROPHY_IMAGE} alt="Troféu" />
                  <BlobIllustration
                    width="100%"
                    height="100%"
                    fill={tournament.color ?? '#e0e0e0'}
                    style={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                    }}
                  />
                </TrophyImageWrapper>

                <TournamentInfo>
                  <TournamentTitle>{tournament.name}</TournamentTitle>
                  {tournament.status && (
                    <StatusSlot>
                      <StatusPill status={tournament.status} />
                    </StatusSlot>
                  )}
                  {dateLabel && <TournamentMeta>{dateLabel}</TournamentMeta>}
                  <ModalitiesLabel>modalidades:</ModalitiesLabel>

                  <ModalitiesRow>
                    {(tournament.modalities ?? []).map(mod => {
                      const modalityName =
                        MODALITY_SLUG_TO_NAME[mod.modality] ?? mod.modality;
                      const imgSrc = MODALITY_IMAGES[mod.modality];
                      const maxRoundsLabel =
                        typeof mod.max_rounds === 'number'
                          ? mod.max_rounds
                          : '-';
                      const roundDurationLabel = formatRoundDuration(
                        mod.time_limit_seconds,
                      );

                      return (
                        <ModalityCard key={mod.id}>
                          {imgSrc && (
                            <ModalityImage src={imgSrc} alt={modalityName} />
                          )}
                          <ModalityInfo>
                            <ModalityName>{modalityName}</ModalityName>
                            <ModalityMeta>{`Rodadas: ${maxRoundsLabel}`}</ModalityMeta>
                            <ModalityMeta>{`Tempo por rodada: ${roundDurationLabel}`}</ModalityMeta>
                            <CategoryBadgeRow>
                              {mod.categories?.map(cat => (
                                <CategoryBadge key={cat.id}>
                                  <MiniBotIcon
                                    width="0.85rem"
                                    strokeWidth={2}
                                    stroke="#1e88e1"
                                  />
                                  {cat.name ??
                                    CATEGORY_NUMBER_TO_LABEL[cat.number]?.split(
                                      ':',
                                    )[0] ??
                                    `Categoria ${cat.number}`}
                                </CategoryBadge>
                              ))}
                            </CategoryBadgeRow>
                          </ModalityInfo>
                        </ModalityCard>
                      );
                    })}
                  </ModalitiesRow>

                  <ActionButtonsRow>
                    <PrimaryButton
                      type="button"
                      onClick={() => navigate(`/tournament/${tournament.id}`)}
                    >
                      Acessar Torneio
                    </PrimaryButton>
                  </ActionButtonsRow>
                </TournamentInfo>
              </MainCardContent>
            </MainCard>
          );
        })}
      </TournamentList>
    </Container>
  );
};

export default ListTournaments;
